/**
 * Viraka LP v2 — mobile sticky CTA + every "back to the grid" control on the
 * page (the close CTA, the sticky button, and each face card).
 *
 * Contract (phase-05):
 *   - The bar appears ONLY once the product grid has scrolled out of view, and
 *     retires again once the close band is on screen, so it never stacks with
 *     the page's final CTA.
 *   - Tapping anything here SCROLLS. It never claims a code and never opens the
 *     cart — the only claim surface on this page is the hero offer card
 *     (assets/afroyla-collection-hero-coupon.js).
 *   - The label flips to the "claimed" string when the hero's claim succeeds
 *     (`afroyla:coupon:claimed`) or when localStorage already carries the flag
 *     that module writes (`afroyla:coupon:<CODE>` === '1').
 *   - Body padding is added only while the bar is on screen, so the page never
 *     shifts on load.
 *
 * Loaded by both sections/afroyla-lp-close.liquid and
 * sections/afroyla-lp-sticky.liquid; the guard below makes the double tag a
 * no-op.
 */
(function () {
  'use strict';

  if (window.__aflLpStickyInit) return;
  window.__aflLpStickyInit = true;

  var GRID_SEL = '[data-afl-grid]';
  var BAR_SEL = '[data-afl-lp-sticky]';
  var CLOSE_SEL = '[data-afl-lp-close]';
  var STICKY_CLASS = 'afl-lp-sticky-on';

  function gridEl() {
    return document.querySelector(GRID_SEL);
  }

  /* ---- scroll-to-grid: every [data-afl-lp-scroll] button on the page ------- */
  function scrollToGrid() {
    var grid = gridEl();
    if (!grid) {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      return;
    }
    grid.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* A face card asks for ONE product's cell. The grid already stamps every cell
     with its handle (`[data-afl-card][data-handle]` in
     sections/afroyla-collection-drop.liquid), so no change is needed there. If
     the handle is not on this page — a filtered grid, a delisted product — fall
     back to the grid itself rather than doing nothing. */
  function scrollToCard(handle) {
    var cell = handle
      ? document.querySelector('[data-afl-card][data-handle="' + handle.replace(/"/g, '') + '"]')
      : null;
    if (!cell || cell.hidden) {
      scrollToGrid();
      return;
    }
    cell.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'center',
    });
    document.querySelectorAll('.afl-lp-target').forEach(function (el) {
      el.classList.remove('afl-lp-target');
    });
    cell.classList.add('afl-lp-target');
    window.setTimeout(function () {
      cell.classList.remove('afl-lp-target');
    }, 2400);
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var card = event.target.closest('[data-afl-lp-scroll-handle]');
    if (card) {
      event.preventDefault();
      scrollToCard(card.getAttribute('data-afl-lp-scroll-handle'));
      return;
    }
    var btn = event.target.closest('[data-afl-lp-scroll]');
    if (!btn) return;
    event.preventDefault();
    scrollToGrid();
  });

  /* ---- the bar ------------------------------------------------------------ */
  function readClaimed(code) {
    if (!code) return false;
    try {
      return window.localStorage.getItem('afroyla:coupon:' + code) === '1';
    } catch (e) {
      return false;
    }
  }

  function paintClaimed(bar) {
    bar.setAttribute('data-state', 'claimed');
    var label = bar.querySelector('[data-afl-lp-sticky-label]');
    var claimed = bar.getAttribute('data-label-claimed');
    if (label && claimed) label.textContent = claimed;
  }

  function initBar(bar) {
    if (bar.__aflLpSticky) return;
    bar.__aflLpSticky = true;

    var code = bar.getAttribute('data-coupon-code');
    if (readClaimed(code)) paintClaimed(bar);

    document.addEventListener('afroyla:coupon:claimed', function (event) {
      var claimedCode = event && event.detail ? event.detail.code : null;
      if (!claimedCode || claimedCode === code) paintClaimed(bar);
    });

    // Two independent gates, combined on every observer tick: the grid must be
    // out of view AND the close band must not have arrived yet. IntersectionObserver
    // is used rather than a scroll listener so nothing runs on the main thread
    // between states.
    var gridOut = false;
    var closeIn = false;

    function apply() {
      var show = gridOut && !closeIn;
      bar.hidden = false;
      bar.setAttribute('data-visible', show ? 'true' : 'false');
      document.documentElement.classList.toggle(STICKY_CLASS, show);
    }

    var grid = gridEl();
    if (!grid || !('IntersectionObserver' in window)) {
      // No grid to track (or no observer support): stay out of the way entirely
      // rather than pinning a bar that cannot know where it is.
      return;
    }

    // No rootMargin: while ANY part of the grid is on screen the bar stays away.
    // A negative bottom margin was tried first and made the bar appear at the
    // fold, on top of the row it was pointing at.
    new IntersectionObserver(function (entries) {
      gridOut = !entries[0].isIntersecting;
      apply();
    }).observe(grid);

    var closeBand = document.querySelector(CLOSE_SEL);
    if (closeBand) {
      new IntersectionObserver(function (entries) {
        closeIn = entries[0].isIntersecting;
        apply();
      }).observe(closeBand);
    }
  }

  function boot() {
    document.querySelectorAll(BAR_SEL).forEach(initBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
